import React from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Responsive from 'src/components/responsive/Responsive';
import { Flex } from 'src/components/flex';
import { conversationTabs, type EConversationTab } from 'src/pages/landlord/conversations/context/types';
import Checkbox from '@mui/material/Checkbox';
import type { IConversation } from 'src/api/landlord-messages-api';
import type { IConversationMatchWithChannel } from 'src/pages/landlord/conversations/context/types';

type Props = {
    tab: EConversationTab;
    setTab: (tab: EConversationTab) => void;
    filteredConversations: IConversation[];
    selectedConversations: Array<IConversation | IConversationMatchWithChannel>;
    setSelectedConversations: React.Dispatch<React.SetStateAction<Array<IConversation | IConversationMatchWithChannel>>>;
};

export default function ConversationsNavigation({
    tab,
    setTab,
    filteredConversations,
    selectedConversations,
    setSelectedConversations,
}: Props) {

    const hasConversations = filteredConversations.length > 0;

    // Check if all filtered conversations are selected
    const allSelected = hasConversations &&
        filteredConversations.every((conversation) => {
            return selectedConversations.some((
                selectedConversation
            ) => selectedConversation.lead_uuid === conversation.lead_uuid);
        });

    // Select/deselect all filtered conversations
    const selectAll = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (allSelected) {
            // Deselect all filtered conversations
            setSelectedConversations(prev => {
                return prev.filter((conversation) => {
                    return !filteredConversations.some((
                        filteredConversation
                    ) => filteredConversation.lead_uuid === conversation.lead_uuid);
                });
            });
        } else {
            // Select all filtered conversations
            setSelectedConversations(prev => {
                const newSelectedConversations = filteredConversations.filter((conversation) => {
                    return !prev.some((
                        selectedConversation
                    ) => selectedConversation.lead_uuid === conversation.lead_uuid);
                });

                return [...prev, ...newSelectedConversations];
            });
        }
    };

    return (
        <Flex
            borderTop="1px solid var(--border-color)"
            bgcolor="common.white"
            justifyContent="space-between"
            alignItems="stretch"
        >
            <Flex alignItems="stretch" columnGap={0}>
                <Responsive desktop>
                    <Flex pl={1} py={1} justifyContent="center" alignItems="center">
                        <Checkbox
                            checked={allSelected}
                            onClick={selectAll}
                            sx={{
                                width: 20,
                                height: 20,
                                '& svg': { fontSize: 20 }
                            }}
                        />
                    </Flex>
                </Responsive>

                <Tabs
                    variant="scrollable"
                    scrollButtons="auto"
                    value={tab}
                    sx={{
                        maxWidth: 'calc(100vw - 16px)',
                        height: '100%',
                        columnGap: 0.5,
                        '& .MuiTabs-flexContainer': { height: '100%' }
                    }}
                >
                    {conversationTabs.map((_tab) => (
                        <Tab
                            key={_tab}
                            label={_tab}
                            value={_tab}
                            onClick={() => setTab(_tab)}
                            iconPosition="end"
                            sx={{ minWidth: 'unset' }}
                        />
                    ))}
                </Tabs>
            </Flex>
        </Flex>
    );
}
